import { escapeHtml } from "@/utils/sanitizeHtml";

/**
 * SALVIA-881 — do resumo para o trecho da transcrição.
 *
 * Clicar numa frase do resumo leva ao ponto da conversa de onde ela saiu.
 *
 * POR QUE POR TEXTO (e não por tempo de áudio): a transcrição é o texto do
 * reconhecimento de voz do navegador, concatenado — sem horário por trecho — e
 * o áudio não é guardado. O resumo vem do serviço de IA sem dizer de onde saiu
 * cada item. O único elo entre os dois é o vocabulário: a frase "Paciente
 * relata cansaço no fim do dia" e o trecho "doutora, eu tenho sentido muito
 * cansaço no fim do dia" dividem "cansaço", "fim", "dia".
 *
 * COMO: a transcrição é cortada em janelas de palavras que se sobrepõem; cada
 * janela recebe uma nota pelas palavras-chave da frase que ela contém, com
 * peso maior para as palavras raras na conversa (IDF) — "losartana" diz muito,
 * "paciente" quase nada. Vence a janela de maior nota, se passar do mínimo.
 *
 * LIMITE HONESTO: frase muito genérica ("Sem outras queixas") pode não achar
 * trecho, e aí a tela diz isso em vez de apontar um lugar errado.
 */

// Palavras que aparecem em qualquer frase e não ajudam a localizar nada.
const STOPWORDS = new Set(
  (
    "a o as os um uma uns umas de do da dos das no na nos nas em ao aos à às " +
    "por pelo pela pelos pelas para pra pro com sem sob sobre entre ate até " +
    "e ou mas que se como quando onde porque pois porem porém entao então ja já " +
    "nao não sim muito muita muitos muitas mais menos bem mal tambem também so só " +
    "eu tu ele ela nos nós vos eles elas voce você voces vocês me te lhe nos lhes " +
    "meu minha meus minhas seu sua seus suas dele dela deles delas isso isto aquilo " +
    "esse essa esses essas este esta estes estas aquele aquela tal " +
    "ser sendo foi era são sao estar esta está estava estao estão tem tinha ter teve " +
    "ha há houve vai vou fazer feito faz fiz pode podia deve " +
    "paciente doutor doutora dr dra consulta relata refere relatou referiu informa " +
    "aqui ali la lá agora hoje dia vez vezes coisa coisas gente tipo assim ne né ok"
  )
    .split(/\s+/)
    .map(normalizeWord)
);

/** Tamanho da janela, em palavras-chave, e o passo entre janelas. */
const WINDOW = 18;
const STEP = 4;

/** Nota mínima (fração do peso da frase) para aceitar o trecho. */
const MIN_COVERAGE = 0.3;

/** Palavras-chave de contexto mostradas antes e depois do trecho que casou. */
const CONTEXT = 2;

/**
 * Números e unidades: o resumo escreve "6 horas", "50 mg", "12x8"; a fala
 * transcrita vem "seis horas", "cinquenta miligramas", "doze por oito".
 */
const NUMBER_WORDS = {
  zero: "0",
  um: "1",
  uma: "1",
  dois: "2",
  duas: "2",
  tres: "3",
  quatro: "4",
  cinco: "5",
  seis: "6",
  sete: "7",
  oito: "8",
  nove: "9",
  dez: "10",
  onze: "11",
  doze: "12",
  treze: "13",
  catorze: "14",
  quatorze: "14",
  quinze: "15",
  dezesseis: "16",
  dezessete: "17",
  dezoito: "18",
  dezenove: "19",
  vinte: "20",
  trinta: "30",
  quarenta: "40",
  cinquenta: "50",
  sessenta: "60",
  setenta: "70",
  oitenta: "80",
  noventa: "90",
  cem: "100",
  cento: "100",
  duzentos: "200",
  trezentos: "300",
  quinhentos: "500",
  mil: "1000",
};
const UNIT_WORDS = {
  mg: "milig",
  miligrama: "milig",
  miligramas: "milig",
  g: "grama",
  kg: "quilo",
  quilos: "quilo",
  ml: "milil",
  mililitros: "milil",
  cm: "centi",
  km: "quilom",
  h: "horas",
  hrs: "horas",
  min: "minut",
};

export function normalizeWord(word) {
  return String(word || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");
}

/**
 * Radical simples: as 5 primeiras letras. Junta "hipertensão" e "hipertenso",
 * "cansaço" e "cansada", sem precisar de dicionário de português.
 */
function stem(word) {
  return word.length > 5 ? word.slice(0, 5) : word;
}

/**
 * Palavras-chave do texto, com a posição de cada uma no texto ORIGINAL.
 *
 * Letras e dígitos viram tokens separados ("12x8" → 12, x, 8; "2x" → 2), e
 * número por extenso vira dígito — senão "seis" nunca casaria com "6".
 */
function keywords(text) {
  const out = [];
  const re = /\p{N}+|\p{L}+/gu;
  let m;
  while ((m = re.exec(text)) !== null) {
    const norm = normalizeWord(m[0]);
    let key;
    if (/^\d+$/.test(norm)) key = String(Number(norm));
    else if (NUMBER_WORDS[norm]) key = NUMBER_WORDS[norm];
    else if (UNIT_WORDS[norm]) key = UNIT_WORDS[norm];
    else if (STOPWORDS.has(norm) || norm.length < 3) continue;
    else key = stem(norm);
    out.push({ stem: key, start: m.index, end: m.index + m[0].length });
  }
  return out;
}

/**
 * Acha o trecho da transcrição que corresponde à frase.
 *
 * @returns {{ start: number, end: number, matched: Set<string> } | null}
 *   posições (no texto da transcrição) do trecho a destacar, ou null.
 */
export function findTranscriptPassage(transcript, phrase) {
  const text = String(transcript || "");
  const words = keywords(text);
  const query = [...new Set(keywords(String(phrase || "")).map((k) => k.stem))];
  if (!words.length || !query.length) return null;

  // Janelas sobrepostas de palavras-chave.
  const windows = [];
  for (let i = 0; i < words.length; i += STEP) {
    windows.push({ from: i, to: Math.min(i + WINDOW, words.length) });
    if (i + WINDOW >= words.length) break;
  }

  // Em quantas janelas cada radical da frase aparece (para o peso IDF).
  const windowStems = windows.map(
    (w) => new Set(words.slice(w.from, w.to).map((k) => k.stem))
  );
  const weight = {};
  for (const s of query) {
    const df = windowStems.filter((set) => set.has(s)).length;
    // Palavra que nao aparece na conversa ("solicitado", vocabulario do
    // resumo) pesa como a mais rara presente, nao mais: senao afundaria o acerto.
    weight[s] = Math.log(1 + windows.length / Math.max(df, 1));
  }
  const total = query.reduce((sum, s) => sum + weight[s], 0);

  let best = null;
  windowStems.forEach((set, idx) => {
    const matched = query.filter((s) => set.has(s));
    const score = matched.reduce((sum, s) => sum + weight[s], 0) / total;
    if (!best || score > best.score) best = { idx, score, matched };
  });

  // Frase curta ("Solicitado hemograma.") se resolve com uma palavra; mais
  // longa, duas: uma palavra solta numa frase longa e coincidencia.
  const minMatches = query.length >= 3 ? 2 : 1;
  if (!best || best.score < MIN_COVERAGE || best.matched.length < minMatches)
    return null;

  // O MENOR trecho da janela que contém todas as palavras que casaram — quando
  // uma palavra aparece duas vezes ("cansaço"... "cansada"), destacar do
  // primeiro ao último acerto pegaria meia conversa.
  // A busca do menor trecho olha um pouco além das bordas da janela: a outra
  // ocorrência de uma palavra pode estar logo depois dela.
  const { from, to } = windows[best.idx];
  const lo = Math.max(0, from - WINDOW / 2);
  const hi = Math.min(words.length, to + WINDOW / 2);
  const matched = new Set(best.matched);
  const hits = [];
  for (let i = lo; i < hi; i++) if (matched.has(words[i].stem)) hits.push(i);
  const [spanStart, spanEnd] = smallestSpan(
    hits,
    (i) => words[i].stem,
    matched.size
  );
  const first = Math.max(0, spanStart - CONTEXT);
  const last = Math.min(words.length - 1, spanEnd + CONTEXT);

  return { start: words[first].start, end: words[last].end, matched };
}

/** Menor intervalo de `hits` (índices em ordem) que cobre `need` radicais distintos. */
function smallestSpan(hits, stemOf, need) {
  const count = new Map();
  let have = 0;
  let best = [hits[0], hits[hits.length - 1]];
  let left = 0;
  for (let right = 0; right < hits.length; right++) {
    const s = stemOf(hits[right]);
    count.set(s, (count.get(s) || 0) + 1);
    if (count.get(s) === 1) have++;
    while (have === need) {
      if (hits[right] - hits[left] < best[1] - best[0])
        best = [hits[left], hits[right]];
      const l = stemOf(hits[left]);
      count.set(l, count.get(l) - 1);
      if (count.get(l) === 0) have--;
      left++;
    }
  }
  return best;
}

/**
 * HTML da transcrição com o trecho marcado (`<mark>`) e as palavras que
 * casaram em negrito. Tudo escapado antes de virar marcação: a transcrição é
 * fala transcrita, e texto nunca vira tag.
 */
export function transcriptHtml(transcript, passage) {
  const text = String(transcript || "");
  if (!passage) return escapeHtml(text);

  const before = text.slice(0, passage.start);
  const inside = text.slice(passage.start, passage.end);
  const after = text.slice(passage.end);

  let marked = "";
  let cursor = 0;
  for (const k of keywords(inside)) {
    marked += escapeHtml(inside.slice(cursor, k.start));
    const word = escapeHtml(inside.slice(k.start, k.end));
    marked += passage.matched.has(k.stem) ? `<strong>${word}</strong>` : word;
    cursor = k.end;
  }
  marked += escapeHtml(inside.slice(cursor));

  return `${escapeHtml(
    before
  )}<mark class="transcript-passage">${marked}</mark>${escapeHtml(after)}`;
}

/**
 * SALVIA-881 — a nota da consulta também entra na transcrição, no ponto da
 * conversa em que foi escrita, para o clique na nota achar o lugar exato.
 *
 * Vai entre colchetes e com o rótulo "Nota:" de propósito: a transcrição vira
 * prontuário e alimenta o resumo da IA, e texto que ninguém falou não pode se
 * passar por fala (regra da salvia-867 no gravador).
 */
export function noteMarker(content) {
  return `[Nota: ${String(content || "")
    .trim()
    .replace(/\s+/g, " ")}]`;
}

/** A transcrição com a marca da nota acrescentada no fim, numa linha própria. */
export function appendNoteMarker(transcript, content) {
  const base = String(transcript || "").replace(/\s+$/, "");
  return `${base ? `${base}\n` : ""}${noteMarker(content)}\n`;
}

/** A transcrição sem a marca da nota (a mais recente, se houver repetidas). */
export function removeNoteMarker(transcript, content) {
  const text = String(transcript || "");
  const marker = noteMarker(content);
  const at = text.lastIndexOf(marker);
  if (at < 0) return text;
  // Leva junto a quebra de linha que a marca ganhou ao entrar.
  const end =
    text[at + marker.length] === "\n"
      ? at + marker.length + 1
      : at + marker.length;
  const start =
    at > 0 && text[at - 1] === "\n" && end === at + marker.length ? at - 1 : at;
  return text.slice(0, start) + text.slice(end);
}

/** Nota mínima (fração do peso da nota) para aceitar a frase do resumo. */
const MIN_SENTENCE_COVERAGE = 0.5;

/**
 * SALVIA-881 — a frase do resumo que corresponde a uma nota ("Ver no resumo").
 *
 * Mesma ideia das janelas da transcrição, mas a unidade já vem pronta: cada
 * frase do resumo. Quem chama passa o título da seção junto com a frase —
 * "Alergias: dipirona" casa com a frase "Dipirona." da seção "Alergias".
 *
 * @param {string[]} sentences textos das frases do resumo (com o título da seção)
 * @returns {number} índice da frase em `sentences`, ou -1 se nenhuma convence
 */
export function bestMatchingSentence(sentences, text) {
  const query = [...new Set(keywords(String(text || "")).map((k) => k.stem))];
  if (!query.length || !sentences.length) return -1;

  const sets = sentences.map(
    (s) => new Set(keywords(String(s || "")).map((k) => k.stem))
  );
  const weight = {};
  for (const s of query) {
    const df = sets.filter((set) => set.has(s)).length;
    // Palavra que não está em frase nenhuma ("pedir") pesa como a mais rara
    // presente, não mais: senão um verbo genérico da nota afundaria o acerto.
    weight[s] = Math.log(1 + sentences.length / Math.max(df, 1));
  }
  const total = query.reduce((sum, s) => sum + weight[s], 0);

  let best = -1;
  let bestScore = 0;
  let bestMatched = 0;
  sets.forEach((set, i) => {
    const matched = query.filter((s) => set.has(s));
    const score = matched.reduce((sum, s) => sum + weight[s], 0) / total;
    if (score > bestScore) {
      best = i;
      bestScore = score;
      bestMatched = matched.length;
    }
  });

  // Nota curta ("Pedir hemograma") se resolve com uma palavra; mais longa, duas.
  const minMatches = query.length >= 3 ? 2 : 1;
  return bestScore >= MIN_SENTENCE_COVERAGE && bestMatched >= minMatches
    ? best
    : -1;
}
